// Step Select Web Component
class StepSelectComponent extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.selectedFilters = {};
    this.totalSteps = 0;
    this.filters = [];
    this.isLoading = false;
    this.stateLoading = false;
    this.showResultsTimeout = null;
    this.searchTimeout = null;
    this.searchResults = {};
  }

  static get observedAttributes() {
    return ['data-filters', 'data-current-step'];
  }

  connectedCallback() {
    this.initializeComponent();
    this.setupEventListeners();
    this.restoreStateFromURL();
  }

  initializeComponent() {
    // Parse filters from data attribute
    const filtersData = this.getAttribute('data-filters');
    if (filtersData) {
      try {
        this.filters = JSON.parse(filtersData);
        this.totalSteps = this.filters.length;
      } catch (error) {
        console.error('Error parsing filters:', error);
        this.filters = [];
        this.totalSteps = 0;
      }
    } else {
      this.filters = [];
      this.totalSteps = 0;
    }

    // Initialize current step
    const currentStepAttr = this.getAttribute('data-current-step');
    if (currentStepAttr) {
      this.currentStep = parseInt(currentStepAttr);
    }

    // Initialize settings
    this.settings = {
      primaryColor: this.getAttribute('data-primary-color') || '#c54343',
      secondaryColor: this.getAttribute('data-secondary-color') || '#0f4c82',
      showProductCount: this.getAttribute('data-show-product-count') === 'true'
    };

    this.render();
  }

  updateStepsProgress() {
    // Update the steps component based on URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const totalSteps = this.filters.length;
    const componentId = `steps-60-${totalSteps}`;
    
    // Count completed steps based on URL parameters
    let completedSteps = 0;
    this.filters.forEach((filter, index) => {
      let hasSelection = false;
      filter.values.forEach(value => {
        if (urlParams.has(value.param_name) && urlParams.get(value.param_name) === value.value) {
          hasSelection = true;
        }
      });
      if (hasSelection) {
        completedSteps++;
      }
    });
    
    if (window.updateStepsProgress) {
      window.updateStepsProgress(componentId, completedSteps);
    }
  }

  updatePreviousStepItems() {
    // Update previous step items for each step (except the first one)
    for (let stepIndex = 1; stepIndex < this.totalSteps; stepIndex++) {
      const prevStepContent = this.querySelector(`#prev-step-content-${stepIndex}`);
      if (!prevStepContent) continue;

      // Clear existing content
      prevStepContent.innerHTML = '';

      // Add items from previous steps
      for (let prevStepIndex = 0; prevStepIndex < stepIndex; prevStepIndex++) {
        const selection = this.selectedFilters[prevStepIndex];
        if (selection) {
          const itemElement = document.createElement('div');
          itemElement.className = 'prev-step-item';
          itemElement.textContent = selection.label;
          prevStepContent.appendChild(itemElement);
        }
      }
    }
  }

  render() {
    // HTML is now rendered by Liquid template
    // Just apply styles and update state
    this.applyStyles();
    this.updateStepDisplay();
    
    // Initialize country tabs - show "country" tab by default for all steps with country tabs
    this.initializeCountryTabs();
  }

  initializeCountryTabs() {
    // Find all country tabs containers and initialize them to show "country" tab
    const countryTabsContainers = this.querySelectorAll('.country-tabs');
    countryTabsContainers.forEach(tabsContainer => {
      const stepIndex = parseInt(tabsContainer.dataset.step);
      // Initialize to show "country" tab (default active tab)
      this.switchCountryTab(stepIndex, 'country');
    });
  }

  applyStyles() {
    const container = this.querySelector('#step-select-container');
    if (!container) return;

    // Set CSS custom properties for colors
    container.style.setProperty('--primary-color', this.settings.primaryColor);
    container.style.setProperty('--secondary-color', this.settings.secondaryColor);
    
    // Calculate darker shades for hover effects
    const primaryColorDark = this.darkenColor(this.settings.primaryColor, 20);
    const secondaryColorDark = this.darkenColor(this.settings.secondaryColor, 20);
    
    container.style.setProperty('--primary-color-dark', primaryColorDark);
    container.style.setProperty('--secondary-color-dark', secondaryColorDark);

  }

  // Helper function to darken a color
  darkenColor(color, percent) {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  getStepClass(stepIndex) {
    if (stepIndex < this.currentStep) return 'completed';
    if (stepIndex === this.currentStep) return 'active';
    return '';
  }

  isSelected(stepIndex, value) {
    return this.selectedFilters[stepIndex] && this.selectedFilters[stepIndex].value === value;
  }


  setupEventListeners() {
    
    // Use document-level event delegation for better reliability
    document.addEventListener('click', (e) => {
      // Check if the click is within this component
      if (!this.contains(e.target)) {
        return;
      }
      
      const option = e.target.closest('.filter-option');
      if (option && !this.isLoading) {
        this.selectOption(option);
        return;
      }

      // Handle navigation buttons
      const button = e.target.closest('.step-button');
      if (button && !this.isLoading) {
        const action = button.dataset.action;
        const step = button.dataset.step;
        
        if (action === 'goToStep' && step !== undefined) {
          this.goToStep(parseInt(step));
        } else if (action === 'backToFilters') {
          this.backToFilters();
        }
      }

      // Handle search input
      const searchInput = e.target.closest('.filter-search-input');
      if (searchInput) {
        this.handleSearchInput(searchInput);
        return;
      }

      // Handle search suggestion clicks
      const suggestion = e.target.closest('.search-suggestion');
      if (suggestion) {
        this.selectSearchSuggestion(suggestion);
        return;
      }

      // Handle country tab clicks
      const countryTab = e.target.closest('.country-tab');
      if (countryTab && !this.isLoading) {
        const tabName = countryTab.dataset.tab;
        const stepIndex = parseInt(countryTab.closest('.country-tabs').dataset.step);
        this.switchCountryTab(stepIndex, tabName);
        return;
      }
    });

    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.restoreStateFromURL();
      this.markSelectedOptions();
    });

    // Also listen for URL changes (for cases where URL is updated programmatically)
    let lastUrl = window.location.href;
    const urlChangeObserver = new MutationObserver(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        this.restoreStateFromURL();
        this.markSelectedOptions()
      }
    });
    
    // Observe changes to the document title (which often changes with URL)
    urlChangeObserver.observe(document, { subtree: true, childList: true });

    // Add input event listeners for search inputs
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('filter-search-input') && this.contains(e.target)) {
        this.handleSearchInput(e.target);
      }
    });

    // Add focus/blur event listeners for search inputs
    document.addEventListener('focus', (e) => {
      if (e.target.classList.contains('filter-search-input') && this.contains(e.target)) {
        const stepIndex = parseInt(e.target.dataset.step);
        const suggestionsContainer = this.querySelector(`.search-suggestions[data-step="${stepIndex}"]`);
        if (suggestionsContainer && suggestionsContainer.innerHTML) {
          suggestionsContainer.style.display = 'block';
        }
      }
    }, true);

    document.addEventListener('blur', (e) => {
      if (e.target.classList.contains('filter-search-input') && this.contains(e.target)) {
        // Delay hiding suggestions to allow clicking on them
        setTimeout(() => {
          const stepIndex = parseInt(e.target.dataset.step);
          const suggestionsContainer = this.querySelector(`.search-suggestions[data-step="${stepIndex}"]`);
          if (suggestionsContainer && !suggestionsContainer.matches(':hover')) {
            suggestionsContainer.style.display = 'none';
          }
        }, 200);
      }
    }, true);
  }

  async selectOption(option) {
    const stepIndex = parseInt(option.dataset.step);
    const value = option.dataset.value;
    const param = option.dataset.param;
    const label = option.dataset.label;

    // Save selection
    this.selectedFilters[stepIndex] = {
      label: label,
      value: value,
      param: param
    };

    // Immediately add selected class for visual feedback
    option.classList.add('selected');

    // Update URL
    this.updateURL();
    
    // Manually trigger DOM update since pushState doesn't trigger popstate
    await this.restoreStateFromURL();
  }

  async updatePageContent() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();

    try {
      const baseUrl = window.location.pathname;
      const params = new URLSearchParams();
      
      // Add selected filters to URL params
      Object.values(this.selectedFilters).forEach(selection => {
        params.append(selection.param, selection.value);
      });

      const newUrl = `${baseUrl}?${params.toString()}`;
      
      // Fetch updated page content
      const response = await fetch(newUrl);
      const responseText = await response.text();
      const newDocument = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Find the step-select-component in the new document
      const newComponent = newDocument.querySelector('step-select-component');
      if (newComponent) {
        // Parse the new filters data
        const newFiltersData = newComponent.getAttribute('data-filters');
        if (newFiltersData) {
          const newFilters = JSON.parse(newFiltersData);
          
          // Update the filters array
          this.filters = newFilters;
          this.totalSteps = this.filters.length;
          
          // Update the entire container with new content
          const newContainer = newDocument.getElementById('step-select-container');
          const currentContainer = this.querySelector('#step-select-container');
          if (newContainer && currentContainer) {
            currentContainer.innerHTML = newContainer.innerHTML;
            this.applyStyles();
          }
          
          // Update step display
          this.updateStepDisplay();
          
          // Re-initialize country tabs after content update
          this.initializeCountryTabs();
        }
      }

    } catch (error) {
      // Handle error silently
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  showProductResults() {
    if (this.showResultsTimeout) {
      clearTimeout(this.showResultsTimeout);
    }
    
    this.showResultsTimeout = setTimeout(() => {
      this._doShowProductResults();
    }, 300);
  }

  async _doShowProductResults() {
    if (window.location.pathname.includes('all_products')) {
      const _url = window.location.href.replace('all_products', 'esims')
      window.location.href = _url
    } else if (window.location.pathname.includes('local-recharge')) {
      const _url = window.location.href.replace('local-recharge', 'charge-products')
      window.location.href = _url
    } else if (window.location.pathname.includes('roaming-recharge')) {
      const _url = window.location.href.replace('roaming-recharge', 'charge-products')
      window.location.href = _url
    }
  }

  backToFilters() {
    // 清除防抖定时器
    if (this.showResultsTimeout) {
      clearTimeout(this.showResultsTimeout);
      this.showResultsTimeout = null;
    }
    
    // Show step content, hide results
    const stepContainer = this.querySelector('.step-content-container');
    const productResults = this.querySelector('.product-results');
    
    if (stepContainer) stepContainer.style.display = 'block';
    if (productResults) productResults.classList.remove('active');
    
    // Clear URL parameters
    window.history.pushState({}, '', window.location.pathname);
    
    // Reset state
    this.selectedFilters = {};
    this.currentStep = 0;
    this.updateStepDisplay();
    
    // Clear selected states in DOM
    this.markSelectedOptions();
  }

  goToStep(stepNumber) {
    if (stepNumber < this.currentStep) {
      // Going back - clear subsequent selections
      Object.keys(this.selectedFilters).forEach(stepIndex => {
        if (parseInt(stepIndex) >= stepNumber) {
          delete this.selectedFilters[stepIndex];
        }
      });

      // Update URL
      this.updateURL();
      
      // Fetch updated content for subsequent steps
      this.updateSubsequentSteps(stepNumber);
    }

    this.currentStep = stepNumber;
    this.updateStepDisplay();
  }

  async updateSubsequentSteps(fromStep) {
    try {
      const baseUrl = window.location.pathname;
      const params = new URLSearchParams();
      
      // Add filters up to the target step
      Object.keys(this.selectedFilters).forEach(stepIndex => {
        if (parseInt(stepIndex) < fromStep) {
          const selection = this.selectedFilters[stepIndex];
          params.append(selection.param, selection.value);
        }
      });

      const newUrl = `${baseUrl}?${params.toString()}`;
      const response = await fetch(newUrl);
      const responseText = await response.text();
      const newDocument = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Update subsequent steps
      for (let i = fromStep; i < this.totalSteps; i++) {
        const newStep = newDocument.querySelector(`.step-content[data-step="${i}"]`);
        const currentStep = this.querySelector(`.step-content[data-step="${i}"]`);
        
        if (newStep && currentStep) {
          currentStep.innerHTML = newStep.innerHTML;
        }
      }
      
      // Re-initialize country tabs after content update
      this.initializeCountryTabs();

    } catch (error) {
      // Handle error silently or show user-friendly message
    }
  }

  updateStepDisplay() {
    // Update step content visibility
    this.querySelectorAll('.step-content').forEach((content, index) => {
      content.classList.toggle('active', index === this.currentStep);
    });

    // Update steps component
    this.updateStepsProgress();
  }

  updateURL() {
    const baseUrl = window.location.pathname;
    const params = new URLSearchParams();
    
    Object.values(this.selectedFilters).forEach(selection => {
      params.append(selection.param, selection.value);
    });

    const newUrl = `${baseUrl}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }

  async restoreStateFromURL() {
    if (this.stateLoading === true) {
      return;
    }
    this.stateLoading = true;
    const urlParams = new URLSearchParams(window.location.search);
    const restoredSelections = {};
    this.filters.forEach((filter, index) => {
      filter.values.forEach(value => {
        if (urlParams.has(value.param_name) && urlParams.get(value.param_name) === value.value) {
          restoredSelections[index] = {
            label: value.label,
            value: value.value,
            param: value.param_name
          };
        }
      });
    });

    this.selectedFilters = restoredSelections;
    
    if (Object.keys(restoredSelections).length > 0) {
      const completedSteps = Object.keys(restoredSelections).map(Number);
      const allStepsCompleted = completedSteps.length === this.totalSteps && 
        completedSteps.every(step => step >= 0 && step < this.totalSteps);
      
      if (allStepsCompleted) {
        this.updateStepsProgress();
        this.showProductResults();
      } else {
        const lastCompletedStep = Math.max(...completedSteps);
        this.currentStep = lastCompletedStep + 1;
        
        if (this.currentStep >= this.totalSteps) {
          this.showProductResults();
        } else {
          this.markSelectedOptions();
          await this.updatePageContent();
        }
      }
    } else {
      this.currentStep = 0;
      
      const stepContainer = this.querySelector('.step-content-container');
      const productResults = this.querySelector('.product-results');
      
      if (stepContainer) {
        stepContainer.style.display = 'block';
      }
      if (productResults) {
        productResults.classList.remove('active');
      }
      
      this.updateStepDisplay();
      this.markSelectedOptions();
      this.updatePreviousStepItems();
      
      // Initialize country tabs when restoring state
      this.initializeCountryTabs();
    }
     this.stateLoading = false;
  }

  markSelectedOptions() {
    // Clear all selected states first
    this.querySelectorAll('.filter-option').forEach(option => {
      option.classList.remove('selected');
    });

    // Clear all select dropdown selections
    this.querySelectorAll('.filter-select').forEach(select => {
      select.selectedIndex = 0;
    });

    // Mark options as selected based on URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    this.filters.forEach((filter, filterIndex) => {
      filter.values.forEach(value => {
        if (urlParams.has(value.param_name) && urlParams.get(value.param_name) === value.value) {
          // Find the corresponding option in the DOM
          const stepContent = this.querySelector(`.step-content[data-step="${filterIndex}"]`);
          if (stepContent) {
            // Handle card options
            const option = stepContent.querySelector(`[data-value="${value.value}"]`);
            if (option) {
              option.classList.add('selected');
            }
            
            // Handle select dropdown
            const select = stepContent.querySelector('.filter-select');
            if (select) {
              const optionElement = select.querySelector(`option[value="${value.value}"]`);
              if (optionElement) {
                optionElement.selected = true;
              }
            }
          }
        }
      });
    });
  }

  showLoading() {
    const loadingElements = this.querySelectorAll('.loading');
    loadingElements.forEach(el => el.style.display = 'block');
  }

  hideLoading() {
    const loadingElements = this.querySelectorAll('.loading');
    loadingElements.forEach(el => el.style.display = 'none');
  }

  // Search functionality methods
  handleSearchInput(searchInput) {
    const stepIndex = parseInt(searchInput.dataset.step);
    const query = searchInput.value.trim().toLowerCase();
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(stepIndex, query);
    }, 300);
  }

  performSearch(stepIndex, query) {
    const suggestionsContainer = this.querySelector(`.search-suggestions[data-step="${stepIndex}"]`);
    const filterOptions = this.querySelectorAll(`.step-content[data-step="${stepIndex}"] .filter-option`);
    
    if (!query) {
      // Clear search results and show all options
      this.clearSearchResults(stepIndex);
      suggestionsContainer.innerHTML = '';
      return;
    }

    const matchingOptions = [];
    const allOptions = [];

    // Collect all options and find matches
    filterOptions.forEach(option => {
      const label = option.dataset.label.toLowerCase();
      const title = option.querySelector('.filter-option-title').textContent.toLowerCase();
      
      allOptions.push({
        element: option,
        label: option.dataset.label,
        value: option.dataset.value,
        param: option.dataset.param,
        step: option.dataset.step
      });

      if (label.includes(query) || title.includes(query)) {
        matchingOptions.push({
          element: option,
          label: option.dataset.label,
          value: option.dataset.value,
          param: option.dataset.param,
          step: option.dataset.step
        });
      }
    });

    // Update search results
    this.searchResults[stepIndex] = {
      query: query,
      matching: matchingOptions,
      all: allOptions
    };

    // Show/hide options based on search
    this.updateFilterOptionsVisibility(stepIndex, matchingOptions);
    
    // Show search suggestions
    this.showSearchSuggestions(stepIndex, matchingOptions);
  }

  updateFilterOptionsVisibility(stepIndex, matchingOptions) {
    const filterOptions = this.querySelectorAll(`.step-content[data-step="${stepIndex}"] .filter-option`);
    
    filterOptions.forEach(option => {
      const isMatch = matchingOptions.some(match => match.value === option.dataset.value);
      option.style.display = isMatch ? 'block' : 'none';
    });
  }

  showSearchSuggestions(stepIndex, matchingOptions) {
    const suggestionsContainer = this.querySelector(`.search-suggestions[data-step="${stepIndex}"]`);
    const lang = document.documentElement.getAttribute('lang')
    if (matchingOptions.length === 0) {
      if (lang === 'ja') {
        suggestionsContainer.innerHTML = '<div class="search-suggestion no-results">該当するオプションが見つかりませんでした​</div>';
      } else {
        suggestionsContainer.innerHTML = '<div class="search-suggestion no-results">No matching options found​</div>';
      }
      suggestionsContainer.style.display = 'block';
      return;
    }

    const suggestionsHTML = matchingOptions.map(option => 
      `<div class="search-suggestion" 
           data-step="${option.step}" 
           data-value="${option.value}" 
           data-param="${option.param}" 
           data-label="${option.label}">
        ${option.label}
      </div>`
    ).join('');

    suggestionsContainer.innerHTML = suggestionsHTML;
    suggestionsContainer.style.display = 'block';
  }

  selectSearchSuggestion(suggestion) {
    const stepIndex = parseInt(suggestion.dataset.step);
    const value = suggestion.dataset.value;
    const param = suggestion.dataset.param;
    const label = suggestion.dataset.label;

    // Find the corresponding option element
    const option = this.querySelector(`.step-content[data-step="${stepIndex}"] .filter-option[data-value="${value}"]`);
    
    if (option) {
      // Clear search
      this.clearSearch(stepIndex);
      
      // Select the option
      this.selectOption(option);
    }
  }

  clearSearch(stepIndex) {
    const searchInput = this.querySelector(`.filter-search-input[data-step="${stepIndex}"]`);
    const suggestionsContainer = this.querySelector(`.search-suggestions[data-step="${stepIndex}"]`);
    
    if (searchInput) {
      searchInput.value = '';
    }
    
    if (suggestionsContainer) {
      suggestionsContainer.innerHTML = '';
      suggestionsContainer.style.display = 'none';
    }
    
    this.clearSearchResults(stepIndex);
  }

  clearSearchResults(stepIndex) {
    // Show all options
    const filterOptions = this.querySelectorAll(`.step-content[data-step="${stepIndex}"] .filter-option`);
    filterOptions.forEach(option => {
      option.style.display = 'block';
    });
    
    // Clear search results
    delete this.searchResults[stepIndex];
  }

  switchCountryTab(stepIndex, tabName) {
    // Update active tab button
    const tabsContainer = this.querySelector(`.country-tabs[data-step="${stepIndex}"]`);
    if (tabsContainer) {
      const tabs = tabsContainer.querySelectorAll('.country-tab');
      let activeTab = null;
      tabs.forEach(tab => {
        if (tab.dataset.tab === tabName) {
          tab.classList.add('active');
          activeTab = tab;
        } else {
          tab.classList.remove('active');
        }
      });
      
      // Scroll active tab to center on mobile
      if (activeTab && window.innerWidth <= 749) {
        this.scrollTabToCenter(tabsContainer, activeTab);
      }
    }

    // Filter options based on tab
    const filterOptions = this.querySelectorAll(`.step-content[data-step="${stepIndex}"] .filter-option`);
    filterOptions.forEach(option => {
      const category = option.dataset.countryCategory || 'all';
      
      if (tabName === 'all') {
        // Show all options
        option.style.display = 'block';
        option.classList.remove('hidden');
      } else {
        // Show only options matching the selected category
        if (category === tabName) {
          option.style.display = 'block';
          option.classList.remove('hidden');
        } else {
          option.style.display = 'none';
          option.classList.add('hidden');
        }
      }
    });
  }

  scrollTabToCenter(container, tab) {
    const containerRect = container.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;
    const tabCenter = tabRect.left + tabRect.width / 2;
    const scrollOffset = tabCenter - containerCenter;
    
    container.scrollBy({
      left: scrollOffset,
      behavior: 'smooth'
    });
  }

  // Override updateStepDisplay to handle search state
  updateStepDisplay() {
    // Update step content visibility
    this.querySelectorAll('.step-content').forEach((content, index) => {
      content.classList.toggle('active', index === this.currentStep);
    });

    // Clear search when switching steps
    this.querySelectorAll('.filter-search-input').forEach(input => {
      const stepIndex = parseInt(input.dataset.step);
      if (stepIndex !== this.currentStep) {
        this.clearSearch(stepIndex);
      }
    });

    // Update previous step items display
    this.updatePreviousStepItems();

    // Update steps component
    this.updateStepsProgress();
  }
}

// Register the custom element when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('step-select-component', StepSelectComponent);
  });
} else {
  customElements.define('step-select-component', StepSelectComponent);
}
